import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── Modelos de co-falla IASH V5.2 — 6 condiciones ────────────────────────────
// Colores: rojo crítico para combinaciones/D1/D5, naranja para D2/D4 (menor severidad)
const COFAILURE_MODELS = [
    { key: 'd1',     label: 'D1 < 60',       subtitle: 'solo Bienestar Físico',     description: 'Fatiga, ignora señales corporales, trabaja lesionado',           multiplier: 2.3, bg: '#E74C3C' },
    { key: 'd5',     label: 'D5 < 60',       subtitle: 'solo Disciplina',           description: 'Omite EPP, saltea pasos, normaliza desvíos',                     multiplier: 1.9, bg: '#E74C3C' },
    { key: 'dual',   label: 'D1+D5 < 60',    subtitle: 'Co-falla crítica',          description: 'Perfil de máxima vulnerabilidad — intervención inmediata',       multiplier: 3.1, bg: '#E74C3C' },
    { key: 'd2',     label: 'D2 < 60',       subtitle: 'solo Manejo Emocional',     description: 'Trabaja bajo estrés sin regulación, impulsividad en riesgo',     multiplier: 1.6, bg: '#E67E22' },
    { key: 'd4',     label: 'D4 < 60',       subtitle: 'solo Comunicación Riesgos', description: 'No reporta condiciones peligrosas, silencia señales de alerta',  multiplier: 1.4, bg: '#F48732' },
    { key: 'triple', label: 'D1+D2+D5 < 60', subtitle: 'Triple co-falla',           description: 'Perfil de emergencia — revisión clínica + intervención urgente', multiplier: 4.2, bg: '#C0392B' },
];

// Colores de perfiles de riesgo IASH — tier de score (espejo del semáforo)
const PROFILE_RISK_COLORS = {
    'Fatigado':       '#C0392B',
    'Impulsivo':      '#E74C3C',
    'Indisciplinado': '#E67E22',
    'Silencioso':     '#F48732',
    'Aislado':        '#F9A825',
    'Distraído':      '#D4AC0D',
    'Desconectado':   '#1E8449',
};
const PROFILE_ORDER = ['Fatigado', 'Impulsivo', 'Indisciplinado', 'Silencioso', 'Aislado', 'Distraído', 'Desconectado'];

// ── Tarjeta de co-falla ──────────────────────────────────────────────────────

const CofailCard = ({ model, count, pct }) => (
    <div className="mgcr-cf-card" style={{ background: model.bg }}>
        <span className="mgcr-cf-card-label">{model.label}</span>
        <span className="mgcr-cf-card-subtitle">({model.subtitle})</span>
        <div className="mgcr-cf-card-mult">{model.multiplier}×</div>
        <div className="mgcr-cf-card-more">más probable</div>
        {count != null && (
            <div className="mgcr-cf-card-count">{count} pers. ({pct}%)</div>
        )}
        <div className="mgcr-cf-card-desc">{model.description}</div>
    </div>
);

// ── Gráfica de barras — CSS puro, sin Recharts ───────────────────────────────

const PCT_SCALE = ['0%','10%','20%','30%','40%','50%','60%','70%','80%','90%','100%'];

const CofailBarChart = ({ profileBars }) => (
    <div className="mgcr-cf-chart-section">
        <div className="mgcr-cf-chart-title">Distribución de perfiles de riesgo</div>
        <div className="mgcr-cf-chart-scale">
            {PCT_SCALE.map(l => <span key={l}>{l}</span>)}
        </div>
        <div className="mgcr-cf-barchart">
            {profileBars.map(({ name, count, pct, color }) => (
                <div key={name} className="mgcr-cf-bar-row">
                    <span className="mgcr-cf-bar-label">{name}</span>
                    <span className="mgcr-cf-bar-ops">{count} ops</span>
                    <div className="mgcr-cf-bar-track">
                        <div className="mgcr-cf-bar-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <span className="mgcr-cf-bar-badge" style={{ background: color }}>{pct}%</span>
                </div>
            ))}
        </div>
    </div>
);

// ── Componente principal ─────────────────────────────────────────────────────

const ManCrossPageCofailIASH = ({ reportData, headerText, pageNumber }) => {
    const cofailData   = reportData?.payload?.iash_cofailure_models  || {};
    const areaProfiles = reportData?.payload?.iash_profiles_by_area  || {};

    // Suma total de perfiles de riesgo IASH a través de todas las áreas
    const profileBars = useMemo(() => {
        const totals = {};
        let grandTotal = 0;
        Object.values(areaProfiles).forEach(areaData => {
            grandTotal += areaData?.total || 0;
            Object.entries(areaData?.profiles || {}).forEach(([name, n]) => {
                if (name !== 'Sin Perfil') totals[name] = (totals[name] || 0) + n;
            });
        });
        return PROFILE_ORDER
            .filter(name => totals[name] > 0)
            .map(name => ({
                name,
                count: totals[name],
                pct:   grandTotal ? Math.round(totals[name] / grandTotal * 100) : 0,
                color: PROFILE_RISK_COLORS[name] || '#9AA2AE',
            }));
    }, [areaProfiles]);

    // Multiplicador más alto activo (para callout spec V4)
    const maxMultiplier = useMemo(() => {
        return COFAILURE_MODELS.reduce((max, m) => (m.multiplier > max ? m.multiplier : max), 0);
    }, []);

    const calloutText = useMemo(() => {
        const n   = cofailData['triple']?.count ?? null;
        const pct = cofailData['triple']?.pct   ?? null;
        const mult = maxMultiplier;
        if (!n) return `El ${mult}× no es una estadística — es la señal de intervención más urgente. Evalúa co-fallas con un plan individual para los casos de mayor riesgo.`;
        return `El ${mult}× no es una estadística — es la señal de intervención más urgente. ${n} personas requieren evaluación individual, no programa grupal.`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cofailData, maxMultiplier]);

    return (
        <div className="mgcr-cf-page">

            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Modelos Predictivos</h2>
                <p  className="mgcr-dx-subtitle">Impacto de Co-falla</p>
            </div>

            {/* Sección 1: panel gris con 5 tarjetas simétricas en fila única */}
            <div className="mgcr-cf-cards-panel">
                {COFAILURE_MODELS.map(m => (
                    <CofailCard
                        key={m.key}
                        model={m}
                        count={cofailData[m.key]?.count ?? null}
                        pct={cofailData[m.key]?.pct   ?? null}
                    />
                ))}
            </div>

            {/* Sección 2: gráfica de perfiles + callout gris */}
            <div className="mgcr-cf-bottom">
                <CofailBarChart profileBars={profileBars} />
                <div className="mgcr-cf-callout-box">
                    <p><strong>Esto implica que:</strong> {calloutText}</p>
                </div>
            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />

        </div>
    );
};

export default ManCrossPageCofailIASH;

